import { Middleware, UnknownAction } from '@reduxjs/toolkit';

import {
  getUserStorageKey,
  safeLocalStorageMerge,
  safeLocalStorageRead,
} from '@/lib/storage/localStorage';
import { selectUserId } from '@/redux/user/selectors';
import { RootState } from '@/shared/types/state';

import { setDeskId } from './actions';

/**
 * Helper function to read stored desk selection for an account
 */
function getStoredDeskId(userId: string | null, accountId: string | null): string | null {
  if (!userId || !accountId) {
    return null;
  }

  const key = getUserStorageKey(userId);
  const storedValue = safeLocalStorageRead(key);

  if (!storedValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(storedValue);
    const deskKey = `selected_desk_id_${accountId}`;
    return parsed[deskKey] || null;
  } catch {
    return null;
  }
}

/**
 * Helper function to persist desk selection to localStorage
 */
function persistDeskSelection(userId: string | null, accountId: string | null, deskId: string) {
  if (!userId || !accountId || !deskId) {
    return;
  }

  const key = getUserStorageKey(userId);
  const deskKey = `selected_desk_id_${accountId}`;
  const value = JSON.stringify({ [deskKey]: deskId });
  safeLocalStorageMerge(key, value);
}

/**
 * Session middleware to handle desk selection persistence
 *
 * This middleware:
 * 1. Restores desk selection from localStorage when account ID is set (if deskId is null)
 * 2. Persists desk selection when it changes
 */
export const sessionMiddleware: Middleware<unknown, RootState> =
  (store) => (next) => (action: UnknownAction) => {
    const stateBefore = store.getState();
    const userId = selectUserId(stateBefore);

    // Let the action pass through to the reducer first
    const result = next(action);

    // Get updated state after action
    const stateAfter = store.getState();
    const accountId = stateAfter.session.account_id;
    const deskId = stateAfter.session.desk_id;

    // When account ID is set, restore desk from localStorage if needed
    if (action.type === 'session/setAccountId' && accountId && !deskId) {
      const storedDeskId = getStoredDeskId(userId, accountId);
      if (storedDeskId) {
        store.dispatch(setDeskId(storedDeskId));
      }
    }

    // When desk ID is set, persist to localStorage
    if (action.type === 'session/setDeskId' && deskId && accountId) {
      persistDeskSelection(userId, accountId, deskId);
    }

    return result;
  };
